import React, { useCallback, useEffect, useState, useRef } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
// Always show the ebook cover, regardless of what the scanner finds
const EBOOK_IMAGE = 'https://cdn.magicpatterns.com/uploads/rdHi3rAEoBrUtDvyQN1xfL/mockup_cenas_antiinf15.png';
// 50/50 split between the two editions
const EDITIONS = ['Pack Completo Antiinflamatorio', 'Cenas Antiinflamatorias'];
const NAMES = ['María', 'Sofía', 'Valentina', 'Lucía', 'Camila', 'Martina', 'Florencia', 'Carolina', 'Gabriela', 'Paula', 'Daniela', 'Romina', 'Andrea', 'Verónica', 'Mariana', 'Agustina', 'Julieta', 'Cecilia', 'Natalia', 'Silvina', 'Patricia', 'Mónica', 'Laura', 'Adriana', 'Claudia'];
const CITIES = ['Buenos Aires', 'Córdoba', 'Rosario', 'Mendoza', 'La Plata', 'San Miguel de Tucumán', 'Mar del Plata', 'Salta', 'Santa Fe', 'San Juan', 'Resistencia', 'Neuquén', 'Posadas', 'Bahía Blanca', 'Paraná', 'Formosa', 'San Luis', 'Río Cuarto', 'Comodoro Rivadavia', 'Concordia'];
interface ToastData {
  id: number;
  name: string;
  city: string;
  edition: string;
  agoText: string;
}
function randomFrom<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}
function randomAgoText(): string {
  // A few seconds up to 9 minutes
  const totalSeconds = Math.floor(Math.random() * (9 * 60 - 5)) + 5;
  if (totalSeconds < 60) {
    return `hace ${totalSeconds} segundos`;
  }
  const minutes = Math.round(totalSeconds / 60);
  return `hace ${minutes} ${minutes === 1 ? 'minuto' : 'minutos'}`;
}
export function RecentPurchaseToast() {
  const prefersReducedMotion = useReducedMotion();
  const [toast, setToast] = useState<ToastData | null>(null);
  const nameHistoryRef = useRef<string[]>([]);
  const idRef = useRef(0);
  const startedRef = useRef(false);
  const timeoutsRef = useRef<number[]>([]);
  const pickName = useCallback(() => {
    const history = nameHistoryRef.current;
    const available = NAMES.filter(n => !history.includes(n));
    const pool = available.length > 0 ? available : NAMES;
    const name = randomFrom(pool);
    history.push(name);
    if (history.length > 10) history.shift();
    return name;
  }, []);
  const showOne = useCallback(() => {
    const data: ToastData = {
      id: ++idRef.current,
      name: pickName(),
      city: randomFrom(CITIES),
      edition: randomFrom(EDITIONS),
      agoText: randomAgoText()
    };
    setToast(data);
    // Visible 4–5s, then dismiss
    const visibleMs = 4000 + Math.random() * 1000;
    const hideTimeout = window.setTimeout(() => {
      setToast(null);
      // Schedule next after random 8–15s gap
      const gapMs = 8000 + Math.random() * 7000;
      const nextTimeout = window.setTimeout(showOne, gapMs);
      timeoutsRef.current.push(nextTimeout);
    }, visibleMs);
    timeoutsRef.current.push(hideTimeout);
  }, [pickName]);
  const start = useCallback(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    // First toast after 3s once the offer stack is reached
    const firstTimeout = window.setTimeout(showOne, 3000);
    timeoutsRef.current.push(firstTimeout);
  }, [showOne]);
  useEffect(() => {
    const target = document.getElementById('offer-section');
    if (!target) return;
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          start();
          observer.disconnect();
        }
      });
    }, {
      threshold: 0.2
    });
    observer.observe(target);
    return () => {
      observer.disconnect();
      timeoutsRef.current.forEach(t => window.clearTimeout(t));
      timeoutsRef.current = [];
    };
  }, [start]);
  const enterAnim = prefersReducedMotion ? {
    opacity: 1,
    y: 0
  } : {
    opacity: 1,
    y: 0
  };
  const initialAnim = prefersReducedMotion ? {
    opacity: 0,
    y: 0
  } : {
    opacity: 0,
    y: 40
  };
  const exitAnim = prefersReducedMotion ? {
    opacity: 0,
    y: 0
  } : {
    opacity: 0,
    y: 40
  };
  return <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[90] w-full max-w-sm px-4 pointer-events-none" aria-live="polite" data-id="element-468">
      <AnimatePresence data-id="element-469">
        {toast && <motion.div key={toast.id} initial={initialAnim} animate={enterAnim} exit={exitAnim} transition={{
        duration: prefersReducedMotion ? 0.15 : 0.4,
        ease: 'easeOut'
      }} className="pointer-events-auto flex items-center gap-3 bg-white rounded-xl shadow-warm border border-forest/10 p-3 pr-4" role="status" data-id="element-470">
            <div className="w-12 h-12 rounded-lg overflow-hidden shrink-0 bg-cream border border-forest/10 flex items-center justify-center" data-id="element-471">
              <img src={EBOOK_IMAGE} alt="Cenas Antiinflamatorias" className="w-full h-full object-cover" data-id="element-472" />
            </div>
            <div className="min-w-0 flex-1" data-id="element-473">
              <p className="text-sm text-dark leading-snug" data-id="element-474">
                <span className="font-bold" data-id="element-475">{toast.name}</span> en{' '}
                <span className="font-medium" data-id="element-476">{toast.city}</span> acaba de
                comprar{' '}
                <span className="font-semibold text-forest" data-id="element-477">
                  {toast.edition}
                </span>
              </p>
              <p className="text-xs text-dark/50 mt-0.5 flex items-center gap-1.5" data-id="element-478">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-success animate-pulse" data-id="element-479" />
                {toast.agoText}
              </p>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}